import { showAuthOnAndroid } from '../state/androidBridge';
import { memberSignInDestination } from './memberOnboarding';
import { signInWithIdentifier } from './signInIdentifier';
import { PasswordInput } from '../components/PasswordInput';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from 'react';
import { Button, TextInput } from '@primer/react';
import { ChevronRight, CheckCircle2 } from 'lucide-react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../components/UI';
import { Skeleton } from '../components/Skeleton';
import { TabUnderline, useSlidingTabs } from '../components/useSlidingTabs';
import { supabase, DEMO_ALLOWED, API_BASE } from '../state/client';
import { readRememberMe, setRememberMe } from '../state/authPersistence';
import { useApp } from '../state/AppContext';
import { profilePath, profileUrl, safeReturnTo } from '@garagegymbros/domain';
import { usernameSchema } from '@garagegymbros/contracts';
import { parseAuthCallback } from './authCallback';
import { completeSignupProfile } from './authProfile';
import { lookupProfileName, useProfileNameAvailability } from './profileNameAvailability';
import {
  destinationAfterMfa,
  requiredTotpFactor,
  resetPasswordWithMfa,
  verifyTotpFactor,
} from './authMfa';
import {
  cssAuthFeaturedImage,
  demoAuthFeaturedCovers,
  loadAuthFeaturedCovers,
  pickAuthFeaturedCover,
  readCachedAuthFeaturedCover,
  writeCachedAuthFeaturedCover,
  type AuthFeaturedCover,
} from './authFeaturedCover';
type AuthForm = 'register' | 'recover' | 'reset' | 'signin' | 'mfa';
export function AuthPage() {
  const location = useLocation(),
    navigate = useNavigate(),
    { mode, session, loading, error: accountError, reload } = useApp();
  const freshSignIn =
    location.pathname === '/auth/signin' &&
    new URLSearchParams(location.search).get('reauth') === '1';
  const [featured, setFeatured] = useState<AuthFeaturedCover | null>(() =>
    readCachedAuthFeaturedCover(),
  );
  useEffect(() => {
    if (featured) return;
    let cancel = false;
    void (async () => {
      const covers =
        mode === 'demo' || !supabase
          ? demoAuthFeaturedCovers()
          : await loadAuthFeaturedCovers(supabase);
      if (cancel) return;
      const picked = pickAuthFeaturedCover(covers);
      if (!picked) return;
      writeCachedAuthFeaturedCover(picked);
      setFeatured(picked);
    })();
    return () => {
      cancel = true;
    };
  }, [featured, mode]);
  const initial = location.pathname.includes('register')
    ? 'register'
    : location.pathname.includes('recover')
      ? 'recover'
      : location.pathname.includes('reset-password')
        ? 'reset'
        : 'signin';
  const [form, setForm] = useState<AuthForm>(initial),
    [email, setEmail] = useState(freshSignIn ? (session?.user.email ?? '') : ''),
    [password, setPassword] = useState(''),
    [name, setName] = useState(''),
    [username, setUsername] = useState(''),
    [age, setAge] = useState(false),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [error, setError] = useState(''),
    [mfaFactorId, setMfaFactorId] = useState(''),
    [mfaCode, setMfaCode] = useState(''),
    [mfaFlow, setMfaFlow] = useState<'signin' | 'reset'>('signin'),
    [rememberMe, setRememberMeChoice] = useState(() => (freshSignIn ? readRememberMe() : true)),
    [resumeError, setResumeError] = useState(''),
    [resumeAttempt, setResumeAttempt] = useState(0);
  useEffect(() => {
    if (freshSignIn || form === 'mfa' || resumeError || (!session && !loading && accountError)) {
      showAuthOnAndroid();
    }
  }, [accountError, form, freshSignIn, loading, resumeError, session]);
  const authTabs = useSlidingTabs(form === 'register' ? 'register' : 'signin');
  const signInEntry =
    !freshSignIn &&
    form === 'signin' &&
    (location.pathname === '/' || location.pathname === '/auth/signin');
  const resuming =
    mode === 'live' &&
    signInEntry &&
    !busy &&
    (loading || Boolean(session) || Boolean(accountError));
  useEffect(() => {
    if (mode !== 'live' || !supabase || !session || loading || busy || !signInEntry) return;
    let cancelled = false;
    setResumeError('');
    const timeout = setTimeout(() => {
      cancelled = true;
      setResumeError('Restoring your sign-in took too long. Please try again.');
    }, 20_000);
    // Restored sessions still complete an enrolled authenticator challenge when required.
    void requiredTotpFactor(supabase).then(
      (factorId) => {
        if (cancelled) return;
        clearTimeout(timeout);
        if (factorId) {
          setMfaFactorId(factorId);
          setMfaFlow('signin');
          setMfaCode('');
          setForm('mfa');
        } else {
          void memberSignInDestination(supabase!, new URLSearchParams(location.search).get('next'))
            .then((destination) => {
              if (!cancelled) navigate(destination, { replace: true });
            })
            .catch((failure) => {
              if (!cancelled)
                setResumeError(
                  failure instanceof Error ? failure.message : 'Welcome setup could not finish.',
                );
            });
        }
      },
      (failure: unknown) => {
        if (!cancelled) {
          clearTimeout(timeout);
          setResumeError(
            failure instanceof Error ? failure.message : 'Your sign-in could not be restored.',
          );
        }
      },
    );
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [
    mode,
    session?.access_token,
    loading,
    busy,
    signInEntry,
    location.search,
    navigate,
    resumeAttempt,
  ]);
  const lookupName = useCallback(
    async (candidate: string) => {
      if (mode !== 'live' || !supabase)
        throw new Error('Exit the local preview to use a live account.');
      return lookupProfileName(supabase, candidate);
    },
    [mode],
  );
  const nameAvailability = useProfileNameAvailability(
    username,
    lookupName,
    form === 'register' && mode === 'live',
  );
  const profileName = usernameSchema.safeParse(username);
  const isLanding = location.pathname === '/' && form === 'signin';
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setMessage('');
    if (mode === 'demo') {
      setError('This is a fictional local preview. Exit the preview to use a live account.');
      return;
    }
    if (!supabase) {
      setError(
        'Live account registration needs Supabase configuration. You can explore the clearly labeled local demo below.',
      );
      return;
    }
    setBusy(true);
    try {
      if (form === 'mfa') {
        await verifyTotpFactor(supabase, mfaFactorId, mfaCode);
        setMfaCode('');
        setMfaFactorId('');
        const destination = destinationAfterMfa(
          mfaFlow,
          new URLSearchParams(location.search).get('next'),
        );
        if (mfaFlow === 'reset') {
          setPassword('');
          setForm('reset');
          setMessage('Authenticator verified. Choose your new password to finish recovery.');
          navigate(destination, { replace: true });
        } else
          navigate(
            await memberSignInDestination(
              supabase,
              new URLSearchParams(location.search).get('next'),
            ),
          );
        return;
      }
      let result;
      if (form === 'register') {
        if (!age) throw new Error('Confirm that you are 18 or older.');
        const candidate = usernameSchema.safeParse(username);
        if (!candidate.success) throw new Error(candidate.error.issues[0].message);
        if (!(await lookupName(candidate.data)))
          throw new Error('That profile name is already taken. Choose another.');
        result = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${locationOrigin()}/auth/callback`,
            data: { username: candidate.data, display_name: name, age_confirmed: true },
          },
        });
        if (!result.error) {
          setMessage(
            'Check your inbox to verify your email and finish claiming your profile name. You must verify before posting or messaging.',
          );
          setPassword('');
        }
      } else if (form === 'recover') {
        result = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${locationOrigin()}/auth/callback?next=/auth/reset-password`,
        });
        if (!result.error)
          setMessage(
            'If an eligible account exists, a recovery email has been requested. Check your inbox.',
          );
      } else if (form === 'reset') {
        const outcome = await resetPasswordWithMfa(supabase, password);
        setPassword('');
        if (outcome.status === 'mfa-required') {
          setMfaFactorId(outcome.factorId);
          setMfaFlow('reset');
          setMfaCode('');
          setForm('mfa');
          setMessage('Verify your authenticator, then choose your new password again.');
        } else {
          setMessage('Password updated.');
          navigate('/home');
        }
        return;
      } else {
        // Prefer durable local storage so a refresh or new tab does not drop the session.
        setRememberMe(rememberMe);
        result = await signInWithIdentifier(supabase, API_BASE, email, password);
        if (!result.error) {
          setRememberMe(rememberMe);
          setPassword('');
          const factorId = await requiredTotpFactor(supabase);
          if (factorId) {
            setMfaFactorId(factorId);
            setMfaFlow('signin');
            setMfaCode('');
            setForm('mfa');
          } else {
            const next = new URLSearchParams(location.search).get('next');
            navigate(await memberSignInDestination(supabase, next));
          }
        }
      }
      if (result.error) throw result.error;
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (mode === 'demo' && location.pathname === '/') return <Navigate to="/home" replace />;
  if (resuming) {
    const failure = resumeError || (!session && !loading ? accountError : '');
    return (
      <div className="callback-page">
        <Logo variant="stacked" />
        <h1>{failure ? 'Your sign-in couldn’t be restored' : 'Checking your sign-in'}</h1>
        {failure ? (
          <>
            <p role="alert">{failure}</p>
            <Button
              onClick={() => {
                setResumeError('');
                if (session) setResumeAttempt((attempt) => attempt + 1);
                else void reload();
              }}
            >
              Try again
            </Button>
          </>
        ) : (
          <Skeleton variant="auth-status" count={1} label="Restoring your sign-in" />
        )}
      </div>
    );
  }
  const featuredImage = featured ? cssAuthFeaturedImage(featured.imageUrl) : '';
  return (
    <div className="auth-layout">
      <div
        className="auth-story"
        style={
          featuredImage ? ({ '--auth-story-image': featuredImage } as CSSProperties) : undefined
        }
      >
        <div className="auth-story-copy">
          <Logo variant="stacked" />
          <h1>Built at home. Never alone.</h1>
        </div>
        <div className="auth-story-bottom">
          {featured ? (
            <Link className="auth-story-credit" to={profilePath(featured.username)}>
              Photo from {featured.displayName}
              ’s gym · garagegymbros.com/{featured.username}
            </Link>
          ) : null}
          <span>GarageGymBros.com</span>
        </div>
      </div>
      <div className="auth-panel">
        <div className="auth-mobile-brand">
          <Logo variant="stacked" />
          <p className="auth-brand-slogan">Built at home. Never alone.</p>
        </div>
        <div className="auth-form-wrap">
          {freshSignIn && form === 'signin' ? (
            <>
              <h2>Verify your identity</h2>
              <p className="form-hint">Sign in again to return to administration.</p>
            </>
          ) : form === 'recover' || form === 'reset' || form === 'mfa' ? (
            <h2>
              {form === 'recover'
                ? 'Let’s get you back in.'
                : form === 'reset'
                  ? 'A fresh start.'
                  : 'One more security check.'}
            </h2>
          ) : (
            <div className="auth-tabs" ref={authTabs.listRef} {...authTabs.listProps}>
              <TabUnderline style={authTabs.indicatorStyle} />
              <button
                className={form === 'signin' ? 'active' : ''}
                {...authTabs.tabProps('signin')}
                onClick={() => {
                  setForm('signin');
                  setError('');
                  setMessage('');
                }}
              >
                Sign in
              </button>
              <button
                className={form === 'register' ? 'active' : ''}
                {...authTabs.tabProps('register')}
                onClick={() => {
                  setForm('register');
                  setError('');
                  setMessage('');
                }}
              >
                Create account
              </button>
            </div>
          )}
          {mode === 'demo' && (
            <p className="inline-notice">
              You are exploring five fictional members. <a href="/home">Continue the preview</a>
              {supabase && (
                <>
                  {' '}
                  or <a href="/?demo=0">return to the live app</a>
                </>
              )}
              .
            </p>
          )}
          <form onSubmit={submit} className="stack-form">
            {form === 'register' && (
              <>
                <label>
                  Display name
                  <TextInput
                    block
                    required
                    autoComplete="name"
                    value={name}
                    maxLength={80}
                    onChange={(e) => setName(e.target.value)}
                  />
                </label>
                <label>
                  Profile name
                  <TextInput
                    block
                    required
                    autoComplete="username"
                    value={username}
                    disabled={busy}
                    maxLength={30}
                    aria-describedby="signup-profile-name-hint signup-profile-name-status"
                    aria-invalid={['invalid', 'taken'].includes(nameAvailability.status)}
                    onChange={(e) => setUsername(e.target.value.toLowerCase())}
                    placeholder="yourname"
                  />
                  <small id="signup-profile-name-hint" className="form-hint">
                    Your shareable link:{' '}
                    <span style={{ overflowWrap: 'anywhere' }}>
                      {profileName.success
                        ? profileUrl(profileName.data).replace('https://', '')
                        : 'garagegymbros.com/yourname'}
                    </span>
                    <br />
                    Use 3–30 lowercase letters, numbers or underscores, starting with a letter.
                  </small>
                  <small
                    id="signup-profile-name-status"
                    role="status"
                    aria-live="polite"
                    className={
                      ['invalid', 'taken', 'error'].includes(nameAvailability.status)
                        ? 'form-error'
                        : 'form-hint'
                    }
                  >
                    {nameAvailability.message}
                  </small>
                </label>
              </>
            )}
            {!['reset', 'mfa'].includes(form) && (
              <label>
                {form === 'signin' ? 'Email or username' : 'Email address'}
                <TextInput
                  block
                  type={form === 'signin' ? 'text' : 'email'}
                  required
                  autoComplete={form === 'signin' ? 'username' : 'email'}
                  autoCapitalize="none"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={form === 'signin' ? 'Email or username' : 'you@example.com'}
                />
              </label>
            )}
            {!['recover', 'mfa'].includes(form) && (
              <label>
                Password
                <PasswordInput
                  block
                  required
                  minLength={form === 'register' || form === 'reset' ? 8 : 1}
                  maxLength={128}
                  autoComplete={form === 'signin' ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {(form === 'register' || form === 'reset') && (
                  <small>Use at least 8 characters.</small>
                )}
              </label>
            )}
            {form === 'mfa' && (
              <label>
                Authenticator code
                <TextInput
                  block
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  minLength={6}
                  maxLength={6}
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, ''))}
                />
              </label>
            )}
            {form === 'signin' && (
              <div className="auth-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) => setRememberMeChoice(event.target.checked)}
                  />
                  Keep me signed in
                </label>
                <button
                  type="button"
                  className="auth-forgot"
                  onClick={() => {
                    setForm('recover');
                    setError('');
                    setMessage('');
                  }}
                >
                  Forgot password?
                </button>
              </div>
            )}
            {form === 'register' && (
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={age}
                  onChange={(e) => setAge(e.target.checked)}
                  required
                />
                <span>
                  I am at least 18 years old and agree to the{' '}
                  <a href="/policies/terms" target="_blank" rel="noopener noreferrer">
                    terms
                  </a>{' '}
                  and{' '}
                  <a href="/policies/community" target="_blank" rel="noopener noreferrer">
                    community guidelines
                  </a>
                  .
                </span>
              </label>
            )}
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            {message && (
              <p className="form-success" role="status">
                <CheckCircle2 size={17} />
                {message}
              </p>
            )}
            <Button
              type="submit"
              className="btn-primary"
              variant="primary"
              block
              disabled={busy || mode === 'demo'}
              trailingVisual={ChevronRight}
            >
              {busy
                ? 'Please wait…'
                : form === 'register'
                  ? 'Create account'
                  : form === 'recover'
                    ? 'Send recovery link'
                    : form === 'reset'
                      ? 'Update password'
                      : form === 'mfa'
                        ? 'Verify and sign in'
                        : 'Sign in'}
            </Button>
            {form === 'signin' && (
              <button
                className="text-link center"
                type="button"
                disabled={mode === 'demo'}
                onClick={async () => {
                  if (mode === 'demo') return;
                  if (!supabase || !email) {
                    setError('Enter your email address to request a verification message.');
                    return;
                  }
                  const { error } = await supabase.auth.resend({
                    type: 'signup',
                    email,
                    options: { emailRedirectTo: `${locationOrigin()}/auth/callback` },
                  });
                  error ? setError(error.message) : setMessage('Verification email requested.');
                }}
              >
                Resend verification email
              </button>
            )}
          </form>
          {DEMO_ALLOWED && mode === 'demo' && (
            <>
              <div className="or-divider">
                <span>TAKE A LOOK AROUND</span>
              </div>
              <Button block onClick={() => navigate('/home')}>
                Explore the local demo
              </Button>
              <p className="form-hint center">
                5 fictional members. Changes stay in this browser.
                <br />
                No account or mailbox is created.
              </p>
            </>
          )}
          <div className="auth-footer">
            <a href="/policies/community">Community guidelines</a>
            <a href="/policies/privacy">Privacy</a>
            <a href="mailto:admin@garagegymbros.com">Contact</a>
          </div>
        </div>
      </div>
    </div>
  );
}
function locationOrigin() {
  return window.location.origin;
}
export function AuthCallback() {
  const { reload, navigate, mode } = useApp(),
    [error, setError] = useState('');
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    async function exchange() {
      if (mode === 'demo') {
        setError('Exit the local preview before using a live account link.');
        return;
      }
      if (!supabase) {
        setError('Account service is not configured.');
        return;
      }
      const {
        code,
        error: callbackError,
        next,
      } = parseAuthCallback(window.location.search, window.location.hash);
      if (callbackError) {
        setError(callbackError);
        return;
      }
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          setError(error.message);
          return;
        }
      }
      let profileNameNeedsSelection = false;
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) profileNameNeedsSelection = await completeSignupProfile(supabase, user);
      } catch {
        /* Profile details remain editable in Settings if onboarding cannot complete. */
      }
      await reload();
      navigate(
        profileNameNeedsSelection
          ? '/settings?tab=profile&profile_name=unavailable'
          : next === '/auth/reset-password'
            ? next
            : await memberSignInDestination(supabase, next),
      );
    }
    void exchange();
  }, []);
  return (
    <div className="callback-page">
      <Logo variant="stacked" />
      <h1>{error ? 'This link could not be used' : 'Signing you in'}</h1>
      {!error && <Skeleton variant="auth-status" count={1} label="Checking your secure link" />}
      {error && (
        <>
          <p role="alert">{error}</p>
          <Button onClick={() => navigate('/auth/signin')}>Back to sign in</Button>
        </>
      )}
    </div>
  );
}
